<template>
  <div
    :class="$q.screen.gt.xs ? 'column justify-between full-height' : 'justify-between q-pb-xl scroll'"
    data-test-id="register-button-page"
    :style="$q.screen.gt.xs ? '' : 'height: 100%;'"
  >
    <template v-if="!$q.screen.gt.sm && editMode">
      Grid can only be edited on a larger screen
    </template>
    <template v-else-if="$q.screen.gt.xs">
      <div
        v-for="(row, rowIndex) in registerGrid.grid"
        :key="rowIndex"
        class="col row overflow-hidden"
      >
        <div
          v-for="(button, columnIndex) in row"
          :key="columnIndex"
          class="col overflow-hidden button"
          style="display: flex; flex-direction: column; justify-content: center;"
        >
          <register-button
            :key="rowIndex * 5 + columnIndex"
            class="overflow-hidden"
            :button="button"
            :index="rowIndex * 5 + columnIndex"
            :edit-mode="editMode"
            :draggable="editMode"
            @drag-start="index => dragStartIndex = index"
            @drag-end="index => dragTo(index)"
            @click="adminApproved => handleClick(button, adminApproved)"
            @add-button="index => add(index)"
            @edit-button="button => edit(button)"
            @delete-button="button => remove(button)"
            @edit-item="item => editItem(item)"
            @edit-grid="$emit('edit-grid', registerGrid)"
            @request-authorization="callback => $emit('request-authorization', callback)"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="col-auto row justify-start items-start q-pt-sm q-pl-sm">
        <div
          v-for="(button, index) in $_.compact(registerGrid.buttonArray)"
          :key="button.id"
          class="col-sm-4 col-xs-6 full-height q-pr-sm q-pb-sm"
          style="display: flex; flex-direction: column;"
        >
          <register-button
            :button="button"
            :index="index"
            :edit-mode="editMode"
            :is-mobile="isMobile"
            :hide-image="hideImage"
            :bg-color="bgColor"
            :text-color="textColor"
            @request-authorization="callback => $emit('request-authorization', callback)"
            @click="adminApproved => handleClick(button, adminApproved)"
          />
        </div>
      </div>
    </template>

    <order-item-form
      ref="orderItemForm"
      @request-authorization="callback => $emit('request-authorization', callback)"
      @show-notification="notifyAdded"
    />

    <register-button-gift-card-dialog
      ref="giftCardDialog"
      @update-gift-card="addGiftCardOrderItem"
    />

    <register-button-misc-item-dialog
      ref="miscItemDialog"
      @update-misc-item="addMiscItemOrderItem"
    />

    <register-button-donation-dialog
      ref="donationDialog"
      @update-donation="addDonationOrderItem"
    />

    <register-button-cashout-dialog
      ref="cashoutDialog"
      @update-cashout="addCashoutOrderItem"
    />

    <register-button-manual-discount-dialog
      ref="manualDiscountDialog"
      @apply-manual-discount="addManualDiscountOrderItems"
    />

    <register-button-form ref="buttonForm" />

    <item-form ref="itemForm" />

    <select-user-form
      ref="selectUserForm"
      @close-without-assigning-user="orderItem => associateCard(orderItem, true)"
    />

    <action-dialog
      ref="assignVoucherDialog"
      title="How do you want to assign this voucher?"
      ok-label="Assign to user"
      cancel-label="Assign to card"
      @ok="assignVoucherToUser"
      @cancel="assignVoucherToCard"
    />

    <q-inner-loading
      v-if="!editMode"
      :showing="registerGrid.loading"
    />
  </div>
</template>

<script>
import { Notify } from 'quasar'
import Card from 'src/models/card/Card'
import { ORDER_ITEM_TYPES } from 'src/models/order-item/OrderItem'
import { BUTTON_TYPES } from 'src/models/register/RegisterButton'
import Voucher from 'src/models/voucher/Voucher'
import ItemForm from 'src/modules/admin/item/ItemForm.vue'
import RegisterButtonForm from 'src/modules/admin/register-grid/components/RegisterButtonForm.vue'
import ActionDialog from 'src/modules/consumer/components/ActionDialog'
import OrderItemForm from 'src/modules/register/components/order-item-form/OrderItemForm'
import RegisterButton from 'src/modules/register/components/register-button/RegisterButton'
import RegisterButtonCashoutDialog from 'src/modules/register/components/register-button/RegisterButtonCashoutDialog'
import RegisterButtonDonationDialog from 'src/modules/register/components/register-button/RegisterButtonDonationDialog'
import RegisterButtonGiftCardDialog from 'src/modules/register/components/register-button/RegisterButtonGiftCardDialog'
import RegisterButtonManualDiscountDialog from 'src/modules/register/components/register-button/RegisterButtonManualDiscountDialog'
import RegisterButtonMiscItemDialog from 'src/modules/register/components/register-button/RegisterButtonMiscItemDialog'
import SelectUserForm from 'src/modules/register/components/SelectUserForm'

export default {
  name: 'RegisterButtonPage',
  components: {
    ActionDialog,
    ItemForm,
    OrderItemForm,
    RegisterButton,
    RegisterButtonCashoutDialog,
    RegisterButtonDonationDialog,
    RegisterButtonGiftCardDialog,
    RegisterButtonForm,
    RegisterButtonMiscItemDialog,
    SelectUserForm,
    RegisterButtonManualDiscountDialog
  },
  configuration: ['itemPackages'],
  inject: ['order'],
  props: {
    registerGrid: {
      type: Object,
      required: true
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false
    },
    showNotification: {
      type: Boolean,
      required: false,
      default: false
    },
    beforeAddUseCallback: {
      type: Boolean,
      required: false,
      default: false
    },
    isMobile: {
      type: Boolean,
      required: false,
      default: false
    },
    hideImage: {
      type: Boolean,
      required: false,
      default: false
    },
    bgColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    }
  },
  emits: ['edit-grid', 'request-authorization', 'before-add'],
  data () {
    return {
      dragStartIndex: null,
      pendingVoucherOrderItem: null
    }
  },
  created () {
    _.extend(this, { BUTTON_TYPES })
  },
  methods: {
    dragTo (dragEndIndex) {
      if (this.dragStartIndex === null || dragEndIndex === null || this.dragStartIndex === dragEndIndex) return
      this.registerGrid.moveButton(this.dragStartIndex, dragEndIndex)
      this.dragStartIndex = null
    },
    handleClick: async function (button, adminApproved = false, isCallback = false) {
      if (this.editMode) {
        this.edit(button)
      } else if (this.beforeAddUseCallback && !isCallback) {
        this.$emit('before-add', () => this.handleClick(button, adminApproved, true))
      } else {
        let orderItem
        if (button.type === BUTTON_TYPES.DISCOUNT) {
          this.order.addIndependentDiscount(button.discount)
          this.order.applyRedemptions()
        } else if (button.type === BUTTON_TYPES.MANUAL_DISCOUNT) {
          this.showManualDiscountDialog(button.discounts)
        } else if (button.type === BUTTON_TYPES.APPLY_VOUCHER) {
          const voucher = new Voucher({ voucherTypeId: button.voucherType.id, isManual: true, autoCreate: true, autoApply: true }, { voucherType: true })
          this.order.addIndependentVoucher(voucher)
          this.order.applyRedemptions()
        } else if (button.type === BUTTON_TYPES.GIFT_CARD) {
          orderItem = button.orderItemForButton()
          this.showGiftCardDialog(orderItem)
        } else if (button.type === BUTTON_TYPES.ITEM_PACKAGE) {
          this.order.addItemPackage(this.itemPackages.get(button.itemPackage.id))
          this.notifyAdded(button.itemPackage)
        } else if (button.type === BUTTON_TYPES.VOUCHER_SALE || button.type === BUTTON_TYPES.VOUCHER_PACKAGE || button.type === BUTTON_TYPES.VOUCHER_SERVICE_ISSUE) {
          this.pendingVoucherOrderItem = button.orderItemForButton()
          this.$refs.assignVoucherDialog.show()
        } else if (button.type === BUTTON_TYPES.ITEM || button.type === BUTTON_TYPES.ITEM_VARIATION) {
          orderItem = button.itemOrItemVariationOrderItemForButton({ order: this.order }, adminApproved)
          if (button.finalSkipDetailDialog) {
            this.order.orderItems.add(orderItem)
            this.notifyAdded(orderItem)
            orderItem.order = this.order
          } else {
            orderItem.order = this.order
            this.$refs.orderItemForm.setCollection(this.order.orderItems)
            this.$refs.orderItemForm.show(orderItem)
          }
        } else if (button.type === BUTTON_TYPES.MISC_ITEM) {
          orderItem = button.orderItemForButton()
          this.showMiscItemDialog(orderItem, button.buttonOptions?.force_tax)
        } else if (button.type === BUTTON_TYPES.DONATION) {
          orderItem = button.orderItemForButton()
          this.showDonationDialog(orderItem)
        } else if (button.type === BUTTON_TYPES.CASH_OUT) {
          orderItem = button.orderItemForButton()
          this.showCashoutDialog(orderItem)
        } else {
          throw new Error('Unhandled button type if register button page click handler: ' + button.type)
        }
        this.order.applyRedemptions() // TODO this should not be needed and should automatically get triggered, adding to address bug. Haven't found root cause
      }
    },
    async assignVoucherToUser () {
      await this.order.orderItems.add(this.pendingVoucherOrderItem)
      this.$refs.selectUserForm.show(this.pendingVoucherOrderItem)
    },
    assignVoucherToCard () {
      this.associateCard(this.pendingVoucherOrderItem)
    },
    async associateCard (orderItem, skipAddOrderItem = false) {
      if (!skipAddOrderItem) {
        await this.order.orderItems.add(orderItem)
      }
      orderItem.order = this.order

      if (orderItem.issuedVoucherPackage?.issuedVoucherPackages?.data?.length) {
        this.associateCardWithVoucherPackages(orderItem)
      } else {
        this.$cardHandler.requestBlankCardScan(
          orderItem.voucherPurchase?.voucherType?.name || orderItem.issuedVoucherPackage?.issuedVoucherPackages?.data[0]?.voucherType?.name || orderItem.issuedVoucherPackage?.voucherPackage?.name || 'Gift Card',
          _.flatten(_.map(orderItem.order.orderItems.data, 'cardUids')),
          (uid) => {
            let cardable
            if (orderItem.type === 'voucher-purchase' || orderItem.type === 'voucher-service-issue') {
              cardable = orderItem.voucherPurchase
            } else if (orderItem.type === 'gift-card') {
              cardable = orderItem.giftCard
            } else if (orderItem.type === ORDER_ITEM_TYPES.VOUCHER_PACKAGE_PURCHASE) {
              cardable = orderItem.issuedVoucherPackage
            } else {
              throw new Error('Unhandled order item type in card assignment: ' + orderItem.type)
            }

            if (uid === false) {
              cardable.skipCard = true
              cardable.card = null
              return
            }

            if (!uid) return

            if (!cardable.validateCardUid(uid)) {
              this.$mh.error('Card does not match item barcode rules')
              this.associateCard(orderItem)
              return
            }

            cardable.skipCard = false
            cardable.card = new Card({ uid })
          }
        )
      }
    },
    associateCardWithVoucherPackages (orderItem, index = 0) {
      const issuedVoucherPackages = orderItem.issuedVoucherPackage.issuedVoucherPackages.data

      if (index >= issuedVoucherPackages.length) return

      this.$cardHandler.requestBlankCardScan(
        issuedVoucherPackages[index].voucherType.name,
        _.flatten(_.map(orderItem.order.orderItems.data, 'cardUids')),
        (uid) => {
          const cardable = issuedVoucherPackages[index]

          if (uid === false) {
            cardable.skipCard = true
            cardable.card = null
          } else if (uid) {
            if (!cardable.validateCardUid(uid)) {
              this.$mh.error('Card does not match item barcode rules')
              this.associateCardWithVoucherPackages(orderItem, index)
              return
            }

            cardable.skipCard = false
            cardable.card = new Card({ uid })
          }

          this.associateCardWithVoucherPackages(orderItem, ++index)
        }
      )
    },
    showGiftCardDialog (orderItem) {
      this.$refs.giftCardDialog.show(orderItem)
    },
    addGiftCardOrderItem (orderItem) {
      if (orderItem.giftCard.initialValue && orderItem.giftCard.initialValue > 0) {
        this.associateCard(orderItem)
      }
    },
    showMiscItemDialog (orderItem, forceTax = false) {
      this.$refs.miscItemDialog.show(orderItem, forceTax)
    },
    addMiscItemOrderItem (orderItem) {
      if (orderItem.amount) {
        this.order.orderItems.add(orderItem)
        this.notifyAdded(orderItem)
      }
    },
    showDonationDialog (orderItem) {
      this.$refs.donationDialog.show(orderItem)
    },
    addDonationOrderItem (orderItem) {
      if (orderItem.amount) {
        this.order.orderItems.add(orderItem)
        this.notifyAdded(orderItem)
      }
    },
    showCashoutDialog (orderItem) {
      this.$refs.cashoutDialog.show(orderItem)
    },
    showManualDiscountDialog (discounts) {
      this.$refs.manualDiscountDialog.show(this.order, discounts)
    },
    addCashoutOrderItem (orderItem) {
      if (orderItem.amount) {
        this.order.orderItems.add(orderItem)
        this.notifyAdded(orderItem)
      }
    },
    addManualDiscountOrderItems (orderItems, discount) {
      _.each(orderItems, orderItem => {
        orderItem.manualDiscount = true
        orderItem.discountId = discount.id
      })
    },
    add (index) {
      this.$refs.buttonForm.setCollection(this.registerGrid.registerButtons)
      this.$refs.buttonForm.show(null, { displayOrder: index })
    },
    edit (button) {
      this.$refs.buttonForm.setCollection(this.registerGrid.registerButtons)
      this.$refs.buttonForm.show(button)
    },
    remove (button) {
      this.registerGrid.registerButtons.remove(button)
    },
    editItem (item) {
      this.$refs.itemForm.show(item)
    },
    notifyAdded (item) {
      if (this.$register && !this.showNotification) return
      const itemName = item.name || item.item.name
      Notify.create({
        message: `${itemName} added to Cart`,
        type: 'positive',
        position: 'bottom',
        group: false,
        timeout: 2500,
        classes: 'added-to-cart-notif'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.buttons {
  display: flex;
  flex-wrap: wrap;
}
.button {
  padding: 10px;
  height: 100%;
}
</style>
